import React, { useState, useEffect } from "react";
import './OpalOrderLayout.css';
import { useNavigate, useParams } from "react-router-dom";
import { Button, Nav, Tab } from "react-bootstrap";
import TauriImage from "../TauriImage/TauriImage";
import appURL from '../../appURL.json';
import TauriLoader2 from "../TauriLoader2/TauriLoader2";
import { useBaseProvider } from "../../BaseManager/BaseManager";
import OpalAlertModal from "../OpalAlertModal/OpalAlertModal";
import { transformOrders, upsertLocalDataOrders } from "./OrderFactoryData/OrderFactoryData";
import TauriCustomPaganation from "../TauriCustomPaganation/TauriCustomPaganation";
import { orderStatus } from "../../visualizations/visualizations";
import CreateOrderModal from "./CreateOrderModal/CreateOrderModal";
import OpalDataTable from "../OpalDataTable/OpalDataTable";
import EditOrderModal from "./EditOrderModal/EditOrderModal";
import OrderDetailsModal from "./OrderDetailsModal/OrderDetailsModal";

const OpalOrderLayout = () => {
    const [search, setSearch] = useState('');
    const [itemList, setItemList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [selectedRow, setSelectedRow] = useState(null);
    const [orderID, setOrderID] = useState(null);
    const [showCreateOrder, setShowCreateOrder] = useState(false);
    const [showEditOrder, setShowEditOrder] = useState(false);
    const [showOrderDetailsModal, setShowOrderDetailsModal] = useState(false);
    const [isCreatingOrder, setIsCreatingOrder] = useState(false);

    const [alertLoading, setAlertLoading] = useState(false);

    const [showAlertModal, setShowAlertModal] = useState(false);
    const [alertModalProps, setAlertModalProps] = useState(null);

    const { isSync } = useBaseProvider();
    const navigate = useNavigate();

    const storedPermission = localStorage.getItem("permission");
    const permission = storedPermission ? JSON.parse(atob(storedPermission)) : null;

    const orderColumns = [
        { key: "OrderNumber", label: "ORDER NO." },
        { key: "OrderDate", label: "ORDER DATE" },
        { key: "Account.Name", label: "ACCOUNT" },
        { key: "NetTotal", label: "NET AMOUNT", inputType: "number" },
        {
            key: "Status",
            label: "STATUS",
            render: row => orderStatusName[row.Status] // assuming you have this map
        }
    ];

    const orderActions = [
        {
            label: "Details",
            onClick: row => handleOrderDetails(row)
        },
        {
            label: "Edit",
            onClick: row => handleEditOrder(row)
        },
        {
            label: "Cancel",
            onClick: row => cancelOrderConfirmation(row)
        }
    ];


    const fetchOrders = async (page = 1, searchTerm = search) => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const devId = localStorage.getItem('devId');

            const today = new Date().toLocaleDateString('en-CA');

            const payload = {
                PageIndex: page,
                Search: searchTerm,
                DateFrom: dateFrom ?? today,
                DateTo: dateTo ?? today
            };

            const response = await fetch(`${appURL.API_GetOrders}/list`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                    'X-ETR-DeviceID': devId
                },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                console.error('Failed to fetch orders');
                setLoading(false);
                return;
            }

            const data = await response.json();
            const transformedOrders = await transformOrders(data.orders || []);
            setItemList(transformedOrders);

            setCurrentPage(data.current_page || 1);
            setTotalPages(data.total_pages || 1);
            setTotalItems(data.total_records || 0);

            await upsertLocalDataOrders(transformedOrders);

        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchOrders(currentPage);
    }, [currentPage]);

    useEffect(() => {
        const delay = setTimeout(() => {
            fetchOrders();
        }, 500);

        return () => clearTimeout(delay);
    }, [search, dateFrom, dateTo]);

    const handleRowClick = (accID) => {
    };

    const handleOrderDetails = (row) => {
        setSelectedRow(row);
        setOrderID(row?.ID);
        setShowOrderDetailsModal(true);
    };

    const handleEditOrder = (row) => {
        if (row?.Status !== orderStatus.Draft) {

            const message = {
                title: "Message",
                bodyText: "This order can only be edited while it is in Draft status.",
                modalType: "message"
            }

            setAlertModalProps(message);
            setShowAlertModal(true);

            return;
        }

        setSelectedRow(row);
        setOrderID(row?.ID);
        setShowEditOrder(true);
    };

    const cancelOrderConfirmation = (row) => {
        if (row?.Status !== orderStatus.Draft) {

            const message = {
                title: "Message",
                bodyText: "This order can only be cancelled in Draft status. Please contact an administrator if the order is already in process.",
                modalType: "message"
            }

            setAlertModalProps(message);
            setShowAlertModal(true);

            return;
        }

        const message = {
            title: "Message",
            bodyText: "Do you want to cancel this order?",
            onConfirm: () => handleCancelOrder(row?.ID),
            confirmButtonText: "OK",
            cancelButtonText: "Cancel",
            modalType: "question"
        }

        setAlertModalProps(message);
        setShowAlertModal(true);

        return;
    }

    const onPageChange = (page) => setCurrentPage(page);

    const handleCreateOrder = async (payload) => {
        setIsCreatingOrder(true);
        try {
            const token = localStorage.getItem('token');
            const devId = localStorage.getItem('devId');

            const response = await fetch(`${appURL.API_CreateOrders}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                    'X-ETR-DeviceID': devId
                },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                console.error('Failed to create orders');
                setIsCreatingOrder(false);
                return;
            }

            const data = await response.json();

            setShowCreateOrder(false);

            setOrderID(data?.orderID);
            setShowEditOrder(true);
            await fetchOrders();
        } catch (error) {
            console.log(error);
        } finally {
            setIsCreatingOrder(false);
        }
    }

    const handleNewOrderClick = () => {
        if (permission?.coverage?.create_orders) {
            setShowCreateOrder(true);
        } else {
            const message = {
                title: "Message",
                bodyText: "You do not have permission to create a new order.",
                modalType: "message"
            };

            setAlertModalProps(message);
            setShowAlertModal(true);
            return;
        };
    };

    const handleCancelOrder = async (OrderID) => {
        setAlertLoading(true);
        try {
            const token = localStorage.getItem('token');
            const devId = localStorage.getItem('devId');

            const response = await fetch(`${appURL.API_CancelOrders}/${OrderID}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                    'X-ETR-DeviceID': devId
                }
            });

            if (!response.ok) {
                console.error('Failed to cancel orders');
                setAlertLoading(false);
                return;
            }

            setShowAlertModal(false);
            await fetchOrders();
        } catch (error) {
            console.log(error);
        } finally {
            setAlertLoading(false);
        }
    }

    const orderStatusName = Object.fromEntries(
        Object.entries(orderStatus).map(([key, value]) => [value, key])
    );

    useEffect(() => {
        const today = new Date().toLocaleDateString('en-CA');
        setDateFrom(today);
        setDateTo(today);
    }, []);

    return (
        <div className="opal-order-layout-container d-flex h-100 row">
            <div className="h-100 p-2">
                <div className="filter-section mb-3">
                    <div className="filter-row search-row">
                        <div className="search-section d-flex align-items-center p-2 w-100">
                            <TauriImage
                                localImage={"/Images/symbols--search.svg"}
                                alt="icon"
                                color={"#7FA6D3"}
                            />
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Type to Search"
                                autoComplete="new-password"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </div>
                    </div>

                    <div className="filter-row action-row">
                        <input
                            type="date"
                            className="form-control"
                            value={dateFrom}
                            onChange={e => setDateFrom(e.target.value)}
                        />

                        <input
                            type="date"
                            className="form-control"
                            value={dateTo}
                            onChange={e => setDateTo(e.target.value)}
                        />

                        {/* <Button
                            className="btn d-flex align-items-center justify-content-center"
                            onClick={handleNewOrderClick}
                        >
                            <TauriImage localImage={"/Images/material-symbols--format-list-bulleted-add.svg"} className="me-1" />
                            <span>New Order</span>
                        </Button> */}
                    </div>
                </div>

                <OpalDataTable
                    loading={loading}
                    data={itemList}
                    columns={orderColumns}
                    actions={orderActions}
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalItems={totalItems}
                    onPageChange={onPageChange}
                    enablePaging={true}
                    onRowClick={handleRowClick}
                />

                <CreateOrderModal
                    show={showCreateOrder}
                    onHide={() => setShowCreateOrder(false)}
                    isCreating={isCreatingOrder}
                    onProceed={handleCreateOrder}
                />
                <EditOrderModal
                    show={showEditOrder}
                    onHide={(changed) => {
                        setShowEditOrder(false);
                        if (changed) {
                            fetchOrders(currentPage);
                        }
                    }}
                    id={orderID}
                />
                <OrderDetailsModal
                    show={showOrderDetailsModal}
                    onHide={() => setShowOrderDetailsModal(false)}
                    id={orderID}
                />
                <OpalAlertModal
                    show={showAlertModal}
                    onHide={() => setShowAlertModal(false)}
                    title={alertModalProps?.title}
                    bodyText={alertModalProps?.bodyText}
                    confirmButtonText={alertModalProps?.confirmButtonText}
                    cancelButtonText={alertModalProps?.cancelButtonText}
                    onConfirm={alertModalProps?.onConfirm}
                    modalType={alertModalProps?.modalType}
                    Icon={alertModalProps?.Icon}
                    loading={alertLoading}
                />
            </div>
        </div>
    );
};

export default OpalOrderLayout;
